import { Signature } from "@/type/core/dashboard/dashboard.types";
import { Picture } from "@/type/core/offices/offices.types";
import { CardDimension } from "@/type/core/signatory/signatory.types";
import { TransactionType } from "@/type/core/transaction/transaction.types";
import SignatoryCardSignedByText from "@components/core/subComponents/SignatoryCard/SignatoryCardSignedByText";
import { getCardDimension } from "@utils/helpers/signatories-helper";
import { FunctionComponent, useLayoutEffect, useRef, useState } from "react";
import useSignatoryCardPicture from "@components/core/subComponents/SignatoryCard/useSignatoryCardPicture";

interface SignatoryCardShadowProps {
  documentRank: number;
  transactionType: TransactionType;
  name?: string;
  email: string;
  firstname: string;
  lastname: string;
  signatorySignaturePictures?: Picture[];
  sealingSignaturePictures?: Picture[];
  signatures: Signature[];
  left: string;
  className?: string;
  officeId?: string;
  isUserInOffice?: boolean;
  signatureIdentificator: string;
}

const SignatoryCardShadow: FunctionComponent<SignatoryCardShadowProps> = ({
  documentRank,
  transactionType,
  name,
  email,
  firstname,
  lastname,
  signatorySignaturePictures,
  sealingSignaturePictures,
  signatures,
  left,
  className = "",
  officeId = "",
  isUserInOffice = false,
  signatureIdentificator,
}) => {
  const observerRef = useRef<MutationObserver | null>(null);
  const signatoryCardShadowRef = useRef<HTMLDivElement>(null);

  const { signature, src } = useSignatoryCardPicture(
    officeId,
    isUserInOffice,
    signatures,
    signatorySignaturePictures,
    sealingSignaturePictures,
    documentRank,
    email,
    transactionType,
    name,
    signatureIdentificator,
  );

  const [dimension, setDimension] = useState<CardDimension>();

  // Grab canvas and compute all patterns at once (when available)
  useLayoutEffect(() => {
    const queryPdf = document.querySelector(
      `#document_${documentRank} .active canvas`,
    ) as HTMLCanvasElement;

    if (queryPdf) {
      setDimension(getCardDimension(queryPdf));
    } else {
      const observer = new MutationObserver(() => {
        const observedPdf = document.querySelector<HTMLCanvasElement>(
          `#document_${documentRank} .pdf .active canvas`,
        );
        if (observedPdf) {
          setDimension(getCardDimension(observedPdf));
          observer.disconnect();
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      observerRef.current = observer;
    }

    return () => {
      observerRef.current?.disconnect();
    };
  }, [documentRank, signature?.pattern]);

  useLayoutEffect(() => {
    const card = signatoryCardShadowRef.current;

    if (card && dimension) {
      card.style.height = `${dimension.height}px`;
      card.style.width = `${dimension.width}px`;
    }
  }, [dimension, transactionType, signature]);

  useLayoutEffect(() => {
    if (signatoryCardShadowRef.current) {
      signatoryCardShadowRef.current.style.left = left;
    }
  }, [left, signature]);

  const shouldRender = !!(signature && dimension);

  if (!shouldRender) return null;

  return (
    <div
      ref={signatoryCardShadowRef}
      data-test="card"
      className={`app-signatory-card shadow position-absolute ${className}`}
      id={`signatoryCardShadow_${email}_${documentRank}`}
    >
      <SignatoryCardSignedByText
        firstname={firstname}
        lastname={lastname}
        cardDimensionWidth={dimension.width}
        transactionType={transactionType}
      />
      <img src={src} className="signatory-image" data-test="card" />
    </div>
  );
};

export default SignatoryCardShadow;
